/**
 * The bid moment.
 *
 * A bid names the lifetime total the listing should hold, and the company pays
 * only the difference between that and what it holds today. The panel shows
 * the position the total would take, what it costs to reach the top of the
 * board, and the amount that will actually be charged — before anyone is sent
 * to a payment page.
 *
 * The rank preview is computed from the board that is already on screen, so it
 * responds to every keystroke without a round trip. The server re-derives the
 * charge from its own record when the session is created; nothing here is
 * trusted with the amount.
 */

import { useMemo, useState, type ReactNode } from 'react';

import {
  MINIMUM_FIRST_BID_CENTS,
  PASS_INCREMENT_CENTS,
  isAwaitingFirstPayment,
  minimumNextTotalCents,
  type Job,
  type LeaderboardScope,
  type RankedEntry,
} from '@bidtohire/shared';

import {
  bidPreview,
  bidSuggestions,
  dollarInputValue,
  parseDollarInput,
} from '../lib/bid-pricing';
import { useCreateCheckout, useLeaderboard } from '../lib/queries';
import { isExpired } from '../lib/format';
import { AmountStepper } from './AmountStepper';
import { Money } from './Money';
import { Odometer } from './Odometer';
import { rememberPublishing } from './PublishedBeat';
import { ErrorNotice } from './Status';
import { CloseIcon, ShieldIcon } from './ui';

/**
 * The rows a bid competes with, read from the first page of the board — the
 * only listings a bid realistically has to clear.
 */
export interface CompetingBoard {
  readonly entries: readonly RankedEntry[];
  readonly isLoading: boolean;
  /**
   * False whenever the board has not been read — loading, or a failed request.
   * A failed read leaves no entries, which is indistinguishable from an empty
   * board unless something says so, and an empty board puts first place at the
   * $5 floor. Nothing derived from the board may be shown as a fact while this
   * is false.
   */
  readonly boardKnown: boolean;
  readonly error: unknown;
  readonly refetch: () => void;
}

export function useCompetingEntries(
  scope: LeaderboardScope,
  excludeJobId: string,
): CompetingBoard {
  const board = useLeaderboard(scope);
  const entries = useMemo(() => {
    const pages = board.data?.pages ?? [];
    return pages
      .flatMap((page) => page.items)
      .filter((entry) => entry.jobId !== excludeJobId);
  }, [board.data, excludeJobId]);

  return {
    entries,
    isLoading: board.isLoading,
    boardKnown: board.data !== undefined,
    error: board.error,
    refetch: () => void board.refetch(),
  };
}

/** Kept for callers that only need the numbers. */
export function useCompetingTotals(
  scope: LeaderboardScope,
  excludeJobId: string,
): { readonly totals: readonly number[]; readonly isLoading: boolean; readonly boardKnown: boolean } {
  const { entries, isLoading, boardKnown } = useCompetingEntries(scope, excludeJobId);
  const totals = useMemo(() => entries.map((entry) => entry.totalPaidCents), [entries]);
  return { totals, isLoading, boardKnown };
}

/* -------------------------------------------------------------------------- */
/* The rank ladder                                                            */
/* -------------------------------------------------------------------------- */

interface Rung {
  readonly key: string;
  readonly name: string;
  readonly totalCents: number;
  readonly isYou: boolean;
}

/**
 * The ladder shows the head of the board with this listing slotted into it at
 * the total being proposed, so the position the money buys is visible rather
 * than described — and the row climbs as the company picks a bigger number.
 */
function buildLadder(
  entries: readonly RankedEntry[],
  jobTitle: string,
  currentTotalCents: number,
  depth = 5,
): readonly Rung[] {
  const rungs: Rung[] = entries.slice(0, depth).map((entry) => ({
    key: entry.jobId,
    name: entry.company.name,
    totalCents: entry.totalPaidCents,
    isYou: false,
  }));
  rungs.push({ key: '__you__', name: jobTitle, totalCents: currentTotalCents, isYou: true });
  rungs.sort((a, b) => b.totalCents - a.totalCents);
  return rungs.slice(0, depth + 1);
}

function RankLadder({ rungs }: { readonly rungs: readonly Rung[] }): ReactNode {
  // The listing directly above yours is what the next dollar has to clear.
  const youIndex = rungs.findIndex((rung) => rung.isYou);
  const targetIndex = youIndex > 0 ? youIndex - 1 : -1;

  return (
    <div className="stage">
      <div className="ladder">
        {rungs.map((rung, index) => {
          const position = index + 1;
          const classes = ['lrow'];
          if (!rung.isYou && index === targetIndex) classes.push('is-target');
          if (rung.isYou) classes.push('is-you');
          // A row the bid has climbed past steps back rather than disappearing:
          // it is still the board, it is just no longer the competition.
          if (youIndex >= 0 && index > youIndex && !rung.isYou) classes.push('is-passed');
          if (position > 4 && !rung.isYou && index !== targetIndex) classes.push('is-faded');

          return (
            <div className={classes.join(' ')} key={rung.key}>
              <span className="row" style={{ gap: 10, minWidth: 0, flexWrap: 'nowrap' }}>
                <span className="pos mono">{position}</span>
                <span className="who">{rung.name}</span>
              </span>
              <span className="row" style={{ gap: 9, flexWrap: 'nowrap' }}>
                {rung.isYou ? (
                  <span className="badge badge-indigo">Your listing</span>
                ) : null}
                {!rung.isYou && index === targetIndex ? (
                  <span className="badge badge-neutral">Next to clear</span>
                ) : null}
                <span className="amt mono">
                  <Money cents={rung.totalCents} />
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The panel                                                                  */
/* -------------------------------------------------------------------------- */

export function BidPanel({
  job,
  scope,
  scopeName,
  onClose,
}: {
  readonly job: Pick<Job, 'id' | 'title' | 'totalPaidCents' | 'status' | 'validThrough'>;
  readonly scope: LeaderboardScope;
  readonly scopeName: string;
  readonly onClose?: () => void;
}): ReactNode {
  const { entries, isLoading, boardKnown, error: boardError, refetch } = useCompetingEntries(
    scope,
    job.id,
  );
  const totals = useMemo(() => entries.map((entry) => entry.totalPaidCents), [entries]);
  const checkout = useCreateCheckout();

  // A draft's bid is not a raise: it is the payment that makes the listing
  // exist publicly at all, and the panel says so rather than implying the
  // listing is already out there.
  const publishing = isAwaitingFirstPayment(job);
  // A lapsed listing is on no board, so a rank preview for it would be
  // counterfactual and the server refuses the bid anyway. Renewal is free and
  // restores the position, so the panel says that instead of quoting a price.
  const expired = !publishing && isExpired(job);
  const suggestions = useMemo(
    () =>
      boardKnown
        ? bidSuggestions({ currentTotalCents: job.totalPaidCents, competingTotalsCents: totals })
        : [],
    [boardKnown, job.totalPaidCents, totals],
  );

  // The field opens at the smallest legal bid. The board does not enter into
  // it: any whole-dollar total clearing this listing's own floor is legal,
  // wherever on the board it lands.
  const legalMinimum = minimumNextTotalCents(job.totalPaidCents);
  const [typed, setTyped] = useState<string | undefined>(undefined);
  const amount = typed ?? dollarInputValue(legalMinimum);
  const setAmount = setTyped;

  const parsed = parseDollarInput(amount);
  const preview = bidPreview({
    currentTotalCents: job.totalPaidCents,
    competingTotalsCents: totals,
    desiredTotalCents: parsed.ok ? parsed.cents : undefined,
  });

  const problem = parsed.ok ? preview.message : parsed.message;
  // An amount that is not yet a legal bid must not be previewed as one: showing
  // the ladder position and the new total for an illegal figure contradicts the
  // message saying the figure cannot be used. Until the bid is valid, both hold
  // at what the listing actually holds today.
  const chosenTotal = parsed.ok && preview.valid ? parsed.cents : undefined;
  const ladder = useMemo(
    () => buildLadder(entries, job.title, chosenTotal ?? job.totalPaidCents),
    [entries, job.title, chosenTotal, job.totalPaidCents],
  );

  async function submit(): Promise<void> {
    if (!parsed.ok || !preview.valid || !boardKnown) return;
    try {
      const session = await checkout.mutateAsync({ jobId: job.id, totalCents: parsed.cents });
      // Stripe returns to the dashboard without saying which listing was paid
      // for; this is how the page that greets the company knows.
      rememberPublishing(job.id);
      window.location.assign(session.checkoutUrl);
    } catch {
      // The failure is already on screen, held by the mutation. Rethrowing
      // would only add an unhandled rejection to the console beside it.
    }
  }

  if (expired) {
    return (
      <section className="modal-grid is-single" aria-label={`Bid on ${job.title}`}>
        <div className="modal-body">
          <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap', gap: 16 }}>
            <div style={{ flexGrow: 1, minWidth: 0 }}>
              <h2>This listing has expired</h2>
              <p className="small muted" style={{ marginTop: 6 }}>
                {job.title} · {scopeName}
              </p>
            </div>
            {onClose === undefined ? null : (
              <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>
                <CloseIcon />
              </button>
            )}
          </div>
          <p style={{ marginTop: 18 }}>
            An expired listing sits on no board, so there is no position to bid for yet. Renew it,
            at no cost, and it returns at the total it already holds — then a raise can move it.
          </p>
          <p className="paynote">
            Renewing is free and keeps the amount paid. The Renew button sits beside this one on
            the dashboard.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="modal-grid" aria-label={`Bid on ${job.title}`}>
      <div className="modal-stage">
        <span className="eyebrow">{scopeName}</span>
        <h2>Where your bid lands</h2>
        <p>
          Bid any amount from <Money cents={MINIMUM_FIRST_BID_CENTS} />. Rank is the total paid, so
          passing a listing takes its total plus <Money cents={PASS_INCREMENT_CENTS} />.
        </p>

        {isLoading ? (
          <div className="stage">
            <div className="skeleton" style={{ height: 52, borderRadius: 8, marginBottom: 10 }} />
            <div className="skeleton" style={{ height: 52, borderRadius: 8, marginBottom: 10 }} />
            <div className="skeleton" style={{ height: 52, borderRadius: 8 }} />
          </div>
        ) : boardKnown ? (
          <RankLadder rungs={ladder} />
        ) : (
          <div className="stage">
            <ErrorNotice error={boardError} onRetry={refetch} />
          </div>
        )}

        <div className="modal-foot">
          <ShieldIcon />
          <span>Payments are processed by Stripe. We never store card details.</span>
        </div>
      </div>

      <div className="modal-body">
        <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap', gap: 16 }}>
          <div style={{ flexGrow: 1, minWidth: 0 }}>
            <h2>{publishing ? 'Publish this listing' : 'Raise your bid'}</h2>
            <p className="small muted" style={{ marginTop: 6 }}>
              {job.title} · {scopeName}
            </p>
          </div>
          {onClose === undefined ? null : (
            <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>
              <CloseIcon />
            </button>
          )}
        </div>

        {publishing ? (
          <p className="small muted" style={{ marginTop: 14 }}>
            This listing is a private draft. It has no public page until this payment of{' '}
            <Money cents={MINIMUM_FIRST_BID_CENTS} /> or more completes.
          </p>
        ) : null}

        <dl className="deflist" style={{ marginTop: 22 }}>
          <dt>Top spot costs</dt>
          <dd data-testid="top-spot-cost">
            {isLoading ? (
              'Checking the board…'
            ) : !boardKnown ? (
              <span className="muted">Couldn’t read the board.</span>
            ) : (
              <>
                <Money cents={preview.firstPlaceTotalCents} />{' '}
                <span className="muted">
                  (the leader’s total plus <Money cents={PASS_INCREMENT_CENTS} />)
                </span>
              </>
            )}
          </dd>
          <dt>{publishing ? 'Smallest bid that publishes' : 'Smallest bid'}</dt>
          <dd>
            {boardKnown ? (
              <Money cents={preview.minimumTotalCents} />
            ) : (
              <span className="muted">Couldn’t read the board.</span>
            )}
          </dd>
        </dl>

        {suggestions.length === 0 ? null : (
          <>
            <span className="eyebrow" style={{ marginTop: 24 }}>
              Positions
            </span>
            <div className="optlist suggestions">
              {suggestions.map((suggestion) => {
                const selected = chosenTotal === suggestion.totalCents;
                return (
                  <button
                    key={suggestion.label}
                    type="button"
                    className={selected ? 'opt is-on' : 'opt'}
                    aria-pressed={selected}
                    onClick={() => setAmount(dollarInputValue(suggestion.totalCents))}
                  >
                    <span className="radio">
                      {selected ? (
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
                          <circle cx="4" cy="4" r="3" fill="#fff" />
                        </svg>
                      ) : null}
                    </span>
                    <span>
                      <span className="title" style={{ display: 'block' }}>
                        {suggestion.label}
                      </span>
                      <span className="detail" style={{ display: 'block' }}>
                        {suggestion.detail}
                      </span>
                    </span>
                    <span className="price">
                      <span className="figure mono" style={{ display: 'block' }}>
                        <Money cents={suggestion.totalCents} />
                      </span>
                      <span className="caption mono">new total</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <div style={{ marginTop: 22 }}>
          <AmountStepper
            label="Lifetime total to hold"
            value={amount}
            onChange={setAmount}
            min={Math.round(preview.minimumTotalCents / 100)}
            hint="Whole dollars. The total the listing holds, not the charge today."
            error={problem}
          />
        </div>

        <div className="receipt">
          <div className="line">
            <span>Already paid on this listing</span>
            <Money cents={job.totalPaidCents} />
          </div>
          <div className="line">
            <span>New cumulative total</span>
            <Money cents={chosenTotal ?? job.totalPaidCents} />
          </div>
          <div className="total">
            <div>
              <div className="label">Charged today</div>
              <div className="note">The difference between the two totals</div>
            </div>
            <span className="figure mono">
              <Odometer cents={preview.valid ? preview.chargeCents : 0} variant="field" />
            </span>
          </div>
        </div>

        <p className="preview-line" data-testid="bid-preview" style={{ marginTop: 14 }}>
          {preview.valid && !boardKnown ? (
            <>
              Charged today: <strong>
                <Money cents={preview.chargeCents} />
              </strong>. The position it takes cannot be shown until the board loads.
            </>
          ) : preview.valid ? (
            <>
              Charged today: <strong>
                <Money cents={preview.chargeCents} />
              </strong>. This listing
              would {publishing ? 'go live at' : 'sit at'}{' '}
              <span className="preview-rank">#{preview.projectedRank}</span> in {scopeName}
              {preview.takesFirstPlace ? ' — the top spot.' : '.'}
            </>
          ) : (
            <span className="muted">
              Enter at least <Money cents={preview.minimumTotalCents} /> to see the position it
              takes.
            </span>
          )}
        </p>

        {checkout.isError ? (
          // Whatever went wrong, the server's own wording is what the company
          // reads: a rejected amount explains itself, and a payments outage
          // says it is temporary rather than looking like a rejected card.
          <div style={{ marginTop: 14 }}>
            <ErrorNotice error={checkout.error} />
          </div>
        ) : null}

        <button
          type="button"
          className="cta"
          style={{ marginTop: 18 }}
          disabled={!preview.valid || !boardKnown || checkout.isPending}
          onClick={() => void submit()}
        >
          {checkout.isPending ? (
            'Opening payment…'
          ) : (
            <>
              Pay <Money cents={preview.valid ? preview.chargeCents : 0} />
              {preview.valid && boardKnown && preview.takesFirstPlace ? ' and take #1' : ''}
            </>
          )}
        </button>

        <p className="paynote">
          Bids are non-refundable. The position updates when the payment clears.
          {publishing ? ' This payment also publishes the listing.' : ''} One payment counts on all
          three boards: global, category, and specialism.
        </p>
      </div>
    </section>
  );
}

/** The panel presented as the artboards' modal, over a dimmed board. */
export function BidModal({
  job,
  scope,
  scopeName,
  onClose,
}: {
  readonly job: Pick<Job, 'id' | 'title' | 'totalPaidCents' | 'status' | 'validThrough'>;
  readonly scope: LeaderboardScope;
  readonly scopeName: string;
  readonly onClose: () => void;
}): ReactNode {
  return (
    <div
      className="modal-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={`Bid on ${job.title}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal">
        <BidPanel job={job} scope={scope} scopeName={scopeName} onClose={onClose} />
      </div>
    </div>
  );
}
